'use client';

import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import { cssEasing, finalFrame, type AnimationKeyframe, type AnimationSpec } from './animation';

/*
  The DOM half of the shared presence animation, played through the Web Animations API.

  WAAPI rather than CSS classes because it takes the same shape the spec is already in — a list of
  keyframes with offsets — so nothing has to be restated as a stylesheet, and because `finished`
  gives the exit a completion to unmount on without an `animationend` listener per element.

  The specs themselves live with the game — see `@puzzlio/stars/components/animations` — so one
  description of a motion serves both renderers.
*/

interface PresenceProps {
  visible: boolean;
  enter: AnimationSpec;
  exit: AnimationSpec;
  /** Replaces `enter` while it is set and the child is already visible — the solved dance. */
  loop?: AnimationSpec;
  children: ReactNode;
}

const transformOf = (frame: AnimationKeyframe): string =>
  `translateX(${frame.translateX ?? 0}px) translateY(${frame.translateY ?? 0}px) scaleX(${frame.scaleX ?? 1}) scaleY(${frame.scaleY ?? 1}) rotate(${frame.rotate ?? 0}deg)`;

/*
  Easing goes on each keyframe, not in the options.

  WAAPI's options-level `easing` spans the whole iteration; a keyframe's own applies from it to the
  next one — which is the CSS behaviour these specs were transcribed from, where
  `animation-timing-function` eases every pair of keyframes in turn. The last frame's is ignored,
  since nothing follows it.
*/
const keyframesOf = (spec: AnimationSpec): Keyframe[] =>
  spec.keyframes.map((frame) => ({
    offset: frame.offset,
    transform: transformOf(frame),
    opacity: frame.opacity ?? 1,
    easing: cssEasing(spec.easing),
  }));

const optionsOf = (spec: AnimationSpec): KeyframeAnimationOptions => ({
  duration: spec.duration,
  delay: spec.delay ?? 0,
  iterations: spec.iterations === 'infinite' ? Infinity : (spec.iterations ?? 1),
  // Linear here on purpose: the easing lives on the keyframes. See keyframesOf.
  easing: 'linear',
  fill: 'both',
});

export function Presence({ visible, enter, exit, loop, children }: PresenceProps) {
  const [mounted, setMounted] = useState(visible);
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visible) setMounted(true);
  }, [visible]);

  useEffect(() => {
    const element = host.current;
    if (!mounted || !element) return;

    // jsdom has no WAAPI. Without it the child simply appears and disappears, which is what a test
    // asserting on the board's contents wants anyway.
    if (typeof element.animate !== 'function') {
      if (!visible) setMounted(false);
      return;
    }

    const spec = visible ? (loop ?? enter) : exit;
    const animation = element.animate(keyframesOf(spec), optionsOf(spec));
    element.style.transformOrigin = spec.origin === 'bottom' ? 'bottom center' : 'center';

    let cancelled = false;
    animation.finished
      .then(() => {
        if (!cancelled && !visible) setMounted(false);
      })
      .catch(() => {
        // A replaced animation rejects; the one that replaced it decides what happens next.
      });

    return () => {
      cancelled = true;
      animation.cancel();
    };
  }, [visible, mounted, enter, exit, loop]);

  if (!mounted) return null;

  // The resting frame is applied inline so the first paint is the entrance's frame 0 rather than the
  // finished glyph — a one-frame flash of a full-size star otherwise.
  const resting = visible ? enter.keyframes[0] : finalFrame(exit);

  return (
    <div
      ref={host}
      style={{
        position: 'absolute',
        inset: 0,
        // React Native's box defaults, as the web Box adopts them, so a `flex: 1` child fills this
        // the way it fills a cell.
        display: 'flex',
        flexDirection: 'column',
        transform: transformOf(resting),
        opacity: resting.opacity ?? 1,
      }}
    >
      {children}
    </div>
  );
}
